import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, Plus, Star, X } from 'lucide-react';

import { formatQuantity, numberFormatter, sumEntries } from '../../utils/calorieLog';

/**
 * The day as a line of meals: eaten ones checked, the next one marked, the free meal starred. A meal opens to what
 * was eaten (with remove), or to what the plan has for it, plus the ways to log it.
 */
export const MealTimeline = ({ meals, nextIndex, isPast, canLog, isOpen, onToggle, onLogPlanned, onAdd, onDelete, isSaving, loggingPlanned }) => {
  const { t, i18n: { language } } = useTranslation();
  const ui = (key, options) => t(`pages.CalorieTracker.ui.${key}`, options);
  const formatNumber = numberFormatter(language);

  return (
    <ul className="mt-cal-tl">
      {meals.map((meal, mealIndex) => {
        const entries = meal.mealEntries;
        const isDone = entries.length > 0;
        const isNext = mealIndex === nextIndex;
        const open = isOpen(mealIndex, isDone);
        const plannedFoods = (meal.foods || []).filter((food) => food.name);
        const plannedKcal = sumEntries(plannedFoods).calories;
        const panelId = `mt-cal-meal-${mealIndex}`;

        let sub;
        if (isDone) {
          const extra = meal.isFreeMeal
            ? meal.budget && ui('freeBudget', { kcal: formatNumber(meal.budget) })
            : plannedKcal > 0 && ui('mealPlan', { kcal: formatNumber(plannedKcal) });
          sub = [ui('foodCount', { count: entries.length }), extra].filter(Boolean).join(' · ');
        } else if (meal.isFreeMeal) sub = ui('freeMealShort');
        else if (isNext) sub = ui('next');
        else sub = isPast ? ui('notLogged') : ui('notYet');

        let kcal = null;
        if (isDone) kcal = ui('kcal', { kcal: formatNumber(meal.totals.calories) });
        else if (meal.isFreeMeal) kcal = meal.budget ? ui('freeBudget', { kcal: formatNumber(meal.budget) }) : null;
        else if (plannedKcal > 0) kcal = ui('kcal', { kcal: formatNumber(plannedKcal) });

        let nodeClass = '';
        if (meal.isFreeMeal) nodeClass = 'is-free';
        else if (isNext) nodeClass = 'is-next';
        if (isDone) nodeClass += ' is-done';

        return (
          <li key={`${mealIndex}-${meal.name}`} className="mt-cal-tl__meal">
            <h3 className="mt-cal-tl__heading">
              <button type="button" className="mt-cal-tl__row" aria-expanded={open} aria-controls={panelId} onClick={() => onToggle(mealIndex, isDone)}>
                <span className={`mt-cal-node ${nodeClass}`} aria-hidden="true">
                  {meal.isFreeMeal ? <Star /> : isDone && <Check />}
                </span>
                <span className="mt-cal-tl__text">
                  <span className="mt-cal-tl__name">{meal.name}</span>
                  <span className="mt-cal-tl__sub">{sub}</span>
                </span>
                <span className={`mt-cal-tl__kcal${isDone ? '' : ' is-muted'}`}>{kcal}</span>
                <ChevronDown className="mt-cal-tl__chevron" aria-hidden="true" />
              </button>
            </h3>

            {open && (
              <div id={panelId} className="mt-cal-tl__body">
                {meal.isFreeMeal && !isDone && (
                  <p className="mt-cal-tl__free">{meal.budget ? ui('freeMealText', { kcal: formatNumber(meal.budget) }) : ui('freeMealTextNoBudget')}</p>
                )}
                {isDone && (
                  <ul className="mt-cal-ents">
                    {entries.map((entry, entryIndex) => (
                      <li key={`${entryIndex}-${entry.name}`} className="mt-cal-ent">
                        <span className="mt-cal-ent__text">
                          <span className="mt-cal-ent__name">{entry.name}</span>
                          {entry.quantity && <span className="mt-cal-ent__qty">{formatQuantity(entry.quantity)}</span>}
                        </span>
                        <span className="mt-cal-ent__kcal">{ui('kcal', { kcal: formatNumber(entry.calories) })}</span>
                        {canLog ? (
                          <button
                            type="button"
                            className="mt-cal-del"
                            onClick={() => onDelete(mealIndex, entryIndex)}
                            disabled={isSaving}
                            aria-label={ui('remove', { name: entry.name })}
                          >
                            <X aria-hidden="true" />
                          </button>
                        ) : (
                          <span />
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                {/* Not eaten yet: what the plan has for it, in gray */}
                {!isDone && plannedFoods.length > 0 && (
                  <ul className="mt-cal-ents mt-cal-ents--plan">
                    {plannedFoods.map((food, index) => (
                      <li key={`${index}-${food.name}`} className="mt-cal-ent">
                        <span className="mt-cal-ent__text">
                          <span className="mt-cal-ent__name">{food.name}</span>
                          {food.quantity && <span className="mt-cal-ent__qty">{formatQuantity(food.quantity)}</span>}
                        </span>
                        <span className="mt-cal-ent__kcal">{ui('kcal', { kcal: formatNumber(food.calories) })}</span>
                        <span />
                      </li>
                    ))}
                  </ul>
                )}
                {canLog && (
                  <div className="mt-cal-tl__actions">
                    {!isDone && plannedFoods.length > 0 && (
                      <button
                        type="button"
                        className="mt-btn mt-cal-btn-sm mt-cal-btn-tint"
                        onClick={() => onLogPlanned(meal, mealIndex)}
                        disabled={isSaving}
                        aria-busy={loggingPlanned === mealIndex || undefined}
                      >
                        {loggingPlanned === mealIndex ? <span className="mt-btn__spinner" aria-hidden="true" /> : <Check aria-hidden="true" />}
                        {ui('logPlanned', { kcal: formatNumber(plannedKcal) })}
                      </button>
                    )}
                    <button
                      type="button"
                      className="mt-btn mt-btn--quiet mt-cal-btn-sm"
                      onClick={() => onAdd(mealIndex, meal.isFreeMeal ? 'search' : 'plan')}
                    >
                      <Plus aria-hidden="true" />
                      {t('pages.CalorieTracker.addFood')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
};
